import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { SrIcon } from "@/components/ui/SrIcon";
import { useDebriefLearnings, type DebriefLearning } from "@/hooks/use-debrief-learnings";
import { useRBAC } from "@/hooks/use-rbac";
import { formatDate } from "@/lib/format";
import { cn } from "@/lib/utils";

function splitToBullets(text: string | null): string[] {
  if (!text) return [];
  return text
    .split(/\r?\n+/)
    .map((line) => line.trim().replace(/^[-*•]\s*/, ""))
    .filter((line) => line.length > 0);
}

interface LearningEntry {
  meeting_id: string;
  project_id: string;
  project_name: string;
  project_code: string | null;
  customer_name: string | null;
  production_lead_name: string | null;
  held_at: string | null;
  bullet: string;
}

function buildEntries(rows: DebriefLearning[], field: "what_went_well" | "what_went_poorly"): LearningEntry[] {
  const out: LearningEntry[] = [];
  for (const row of rows) {
    for (const bullet of splitToBullets(row[field])) {
      out.push({
        meeting_id: row.meeting_id,
        project_id: row.project_id,
        project_name: row.project_name,
        project_code: row.project_code,
        customer_name: row.customer_name,
        production_lead_name: row.production_lead_name,
        held_at: row.held_at,
        bullet,
      });
    }
  }
  return out;
}

function LearningColumn({
  title,
  tone,
  entries,
  searchQuery,
}: {
  title: string;
  tone: "good" | "bad";
  entries: LearningEntry[];
  searchQuery: string;
}) {
  const filtered = useMemo(() => {
    if (!searchQuery.trim()) return entries;
    const q = searchQuery.toLowerCase();
    return entries.filter(
      (e) =>
        e.bullet.toLowerCase().includes(q) ||
        e.project_name.toLowerCase().includes(q) ||
        (e.customer_name ?? "").toLowerCase().includes(q) ||
        (e.production_lead_name ?? "").toLowerCase().includes(q),
    );
  }, [entries, searchQuery]);

  return (
    <Card>
      <CardContent className="p-4 space-y-3">
        <div className="flex items-center justify-between">
          <h2
            className={cn(
              "font-display text-lg",
              tone === "good" ? "text-emerald-700" : "text-amber-700",
            )}
          >
            {title}
          </h2>
          <span className="text-xs text-muted-foreground">{filtered.length}</span>
        </div>
        {filtered.length === 0 ? (
          <p className="text-sm text-muted-foreground py-4">Ingen oppføringer.</p>
        ) : (
          <ul className="space-y-3">
            {filtered.map((entry, idx) => (
              <li
                key={`${entry.meeting_id}-${idx}`}
                className={cn(
                  "border-l-2 pl-3 py-1",
                  tone === "good" ? "border-emerald-300" : "border-amber-300",
                )}
              >
                <p className="text-sm">{entry.bullet}</p>
                <p className="text-xs text-muted-foreground mt-1">
                  <Link
                    to={`/prosjekter/${entry.project_id}`}
                    className="hover:text-primary hover:underline"
                  >
                    {entry.project_code && `${entry.project_code} `}
                    {entry.project_name}
                  </Link>
                  {entry.customer_name && ` · ${entry.customer_name}`}
                  {entry.production_lead_name && ` · ${entry.production_lead_name}`}
                  {entry.held_at && ` · ${formatDate(entry.held_at)}`}
                </p>
              </li>
            ))}
          </ul>
        )}
      </CardContent>
    </Card>
  );
}

export default function LaeringPage() {
  const { isAdmin } = useRBAC();
  const { data: rows = [], isLoading } = useDebriefLearnings();
  const [searchQuery, setSearchQuery] = useState("");

  // TODO: gates "Hva gikk dårlig" på PL+admin. Inntil vi har en god måte å
  // sjekke "er PL på minst ett prosjekt", bruker vi isAdmin som proxy.
  const canSeeNegative = isAdmin;

  const wellEntries = useMemo(() => buildEntries(rows, "what_went_well"), [rows]);
  const poorlyEntries = useMemo(() => buildEntries(rows, "what_went_poorly"), [rows]);

  return (
    <div className="page-container animate-fade-in">
      <div className="flex items-center gap-2 mb-2">
        <SrIcon name="sparkles" className="w-6 h-6 text-primary" />
        <h1 className="font-display text-2xl">Læringsbibliotek</h1>
      </div>
      <p className="text-sm text-muted-foreground mb-6 max-w-2xl">
        Aggregert læring fra debriefer på tvers av alle prosjekter. Bidra ved å
        fylle ut "Hva gikk bra" og "Hva gikk dårlig" i debrief-dialogen for
        hvert prosjekt.
      </p>

      <Card className="mb-4">
        <CardContent className="p-4">
          <div className="flex items-center gap-2">
            <SrIcon name="search" className="w-4 h-4 text-muted-foreground shrink-0" />
            <Input
              placeholder="Søk i tekst, prosjekt, kunde eller PL..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="h-9 text-sm border-0 shadow-none focus-visible:ring-0 px-0"
            />
          </div>
        </CardContent>
      </Card>

      {isLoading ? (
        <div className="flex justify-center py-12">
          <SrIcon name="loader2" className="w-5 h-5 animate-spin text-muted-foreground" />
        </div>
      ) : (
        <div className={cn("grid gap-4", canSeeNegative ? "lg:grid-cols-2" : "lg:grid-cols-1 max-w-2xl")}>
          <LearningColumn
            title="Hva gikk bra"
            tone="good"
            entries={wellEntries}
            searchQuery={searchQuery}
          />
          {canSeeNegative && (
            <LearningColumn
              title="Hva gikk dårlig"
              tone="bad"
              entries={poorlyEntries}
              searchQuery={searchQuery}
            />
          )}
        </div>
      )}
    </div>
  );
}
